import { useCallback, useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react'
import { DashboardLayout } from '@/components/layout'
import { Card, CardContent } from '@/components/ui/card'
import {
  type CandidateFacets, type CandidateRow, type CandidateSort,
  getCandidateFacets, listCandidates,
} from '@/lib/api'
import { CandidateDrawer, type DrawerCandidate } from '@/lib/CandidateDrawer'
import { atsNav } from '@/lib/atsNav'
import { Avatar, PageHeader } from '@/lib/atsUi'
import { currentTenant, currentUser } from '@/lib/mocks'

export const Route = createFileRoute('/candidates/')({ component: CandidatesPage })

const fmtExp = (m: number | null) => (m == null ? '—' : `${Math.floor(m / 12)}y ${m % 12}m`)
const EXP_OPTS = [1, 2, 3, 5, 7, 10, 12, 15, 20]
const PAGE_SIZE = 25

const inputCls = 'h-9 rounded-md border border-input bg-card px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring'

function CandidatesPage() {
  const [rows, setRows] = useState<CandidateRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [facets, setFacets] = useState<CandidateFacets | null>(null)

  // filters
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [skill, setSkill] = useState('')
  const [location, setLocation] = useState('')
  const [minExp, setMinExp] = useState(0)
  const [maxExp, setMaxExp] = useState(0)
  const [sort, setSort] = useState<CandidateSort>('recent')
  const [page, setPage] = useState(0)
  const [openCand, setOpenCand] = useState<DrawerCandidate | null>(null)

  useEffect(() => {
    getCandidateFacets().then(setFacets).catch(() => {})
  }, [])

  // debounce the free-text search
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), 300)
    return () => clearTimeout(id)
  }, [search])

  // any filter change (except page) resets to page 0
  useEffect(() => {
    setPage(0)
  }, [debounced, skill, location, minExp, maxExp, sort])

  useEffect(() => {
    let live = true
    setLoading(true)
    listCandidates({
      search: debounced || undefined, skill: skill || undefined, location: location || undefined,
      minExp: minExp || undefined, maxExp: maxExp || undefined, sort, page, pageSize: PAGE_SIZE,
    })
      .then((res) => {
        if (!live) return
        setRows(res.candidates)
        setTotal(res.total)
      })
      .finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [debounced, skill, location, minExp, maxExp, sort, page])

  const activeFilters = useMemo(
    () => [debounced && 'search', skill, location, minExp && 'minExp', maxExp && 'maxExp'].filter(Boolean).length,
    [debounced, skill, location, minExp, maxExp],
  )
  const clearAll = useCallback(() => {
    setSearch(''); setDebounced(''); setSkill(''); setLocation(''); setMinExp(0); setMaxExp(0); setSort('recent')
  }, [])

  // clicking a sortable header toggles/sets the sort
  const sortBy = (key: 'exp' | 'name') => {
    if (key === 'name') setSort('name')
    else setSort(sort === 'expDesc' ? 'expAsc' : 'expDesc')
  }
  const sortArrow = (key: 'exp' | 'name') => {
    if (key === 'name') return sort === 'name' ? ' ↑' : ''
    return sort === 'expDesc' ? ' ↓' : sort === 'expAsc' ? ' ↑' : ''
  }

  const from = total === 0 ? 0 : page * PAGE_SIZE + 1
  const to = Math.min((page + 1) * PAGE_SIZE, total)
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1)

  return (
    <DashboardLayout title="Candidates" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <PageHeader title="Candidates" subtitle="Your live candidate database — search, filter, and sort across the full pool." />

      {/* filter toolbar */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, company, location, or skill…"
            className={`${inputCls} w-full pl-8`}
          />
        </div>

        <select value={skill} onChange={(e) => setSkill(e.target.value)} className={inputCls} aria-label="Filter by skill">
          <option value="">All skills</option>
          {facets?.skills.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <select value={location} onChange={(e) => setLocation(e.target.value)} className={inputCls} aria-label="Filter by location">
          <option value="">All locations</option>
          {facets?.locations.map((l) => (
            <option key={l.value} value={l.value}>{l.value} ({l.count.toLocaleString()})</option>
          ))}
        </select>

        <select value={minExp} onChange={(e) => setMinExp(Number(e.target.value))} className={inputCls} aria-label="Minimum experience">
          <option value={0}>Min exp</option>
          {EXP_OPTS.map((y) => (
            <option key={y} value={y}>{y}+ yrs</option>
          ))}
        </select>
        <select value={maxExp} onChange={(e) => setMaxExp(Number(e.target.value))} className={inputCls} aria-label="Maximum experience">
          <option value={0}>Max exp</option>
          {EXP_OPTS.map((y) => (
            <option key={y} value={y}>≤ {y} yrs</option>
          ))}
        </select>

        <select value={sort} onChange={(e) => setSort(e.target.value as CandidateSort)} className={inputCls} aria-label="Sort">
          <option value="recent">Most recent</option>
          <option value="expDesc">Experience: high → low</option>
          <option value="expAsc">Experience: low → high</option>
          <option value="name">Name: A → Z</option>
        </select>

        {activeFilters > 0 && (
          <button onClick={clearAll} className="inline-flex h-9 items-center gap-1 rounded-md border border-input bg-card px-2.5 text-sm text-muted-foreground hover:bg-muted/50">
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        )}
      </div>

      <div className="mb-2 text-xs text-muted-foreground">
        {loading ? 'Loading…' : `${total.toLocaleString()} candidate${total === 1 ? '' : 's'}${activeFilters > 0 ? ' match your filters' : ''}`}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="cursor-pointer select-none px-4 py-2.5 font-medium hover:text-foreground" onClick={() => sortBy('name')}>
                    Candidate{sortArrow('name')}
                  </th>
                  <th className="px-4 py-2.5 font-medium">Company</th>
                  <th className="cursor-pointer select-none px-4 py-2.5 font-medium hover:text-foreground" onClick={() => sortBy('exp')}>
                    Experience{sortArrow('exp')}
                  </th>
                  <th className="px-4 py-2.5 font-medium">Location</th>
                  <th className="px-4 py-2.5 font-medium">Skills</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-sm text-muted-foreground">
                      No candidates match these filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => setOpenCand({ candidateId: c.id, name: c.name, employer: c.employer, totalMonthsExp: c.totalMonthsExp, location: c.location })}
                      className="group cursor-pointer transition-colors hover:bg-muted/40"
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <Avatar name={c.name ?? '?'} className="h-7 w-7 shrink-0 text-[10px]" />
                          <span className="font-medium text-foreground group-hover:underline">{c.name ?? '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">{c.employer ?? '—'}</td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground">{fmtExp(c.totalMonthsExp)}</td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground">{c.location ?? '—'}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex max-w-[320px] flex-wrap gap-1">
                          {c.skills.slice(0, 4).map((s) => (
                            <span key={s} className="inline-flex items-center rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[11px] text-muted-foreground">
                              {s}
                            </span>
                          ))}
                          {c.skills.length > 4 && (
                            <span className="inline-flex items-center px-1 text-[11px] text-muted-foreground">+{c.skills.length - 4}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* pagination */}
          <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
            <span>{total === 0 ? 'No results' : `Showing ${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}`}</span>
            <div className="flex items-center gap-1">
              <button
                disabled={page === 0 || loading}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="inline-flex h-7 items-center gap-0.5 rounded-md border border-input bg-card px-2 disabled:opacity-40 enabled:hover:bg-muted/50"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </button>
              <span className="px-1.5">Page {page + 1} of {(lastPage + 1).toLocaleString()}</span>
              <button
                disabled={page >= lastPage || loading}
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                className="inline-flex h-7 items-center gap-0.5 rounded-md border border-input bg-card px-2 disabled:opacity-40 enabled:hover:bg-muted/50"
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      <CandidateDrawer cand={openCand} onClose={() => setOpenCand(null)} />
    </DashboardLayout>
  )
}
